import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  MapPin,
  Phone,
  Mail,
  ArrowUp
} from 'lucide-react'
import SocialButtons from '../ui/SocialButtons'

const Footer = () => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Supprimé socialLinks statiques - maintenant géré par SocialButtons

  const quickLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.gallery'), path: '/gallery' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.contact'), path: '/contact' },
  ]

  return (
    <footer className="relative mt-20">
      <div className="bg-primary-500 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* About Section */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://raw.githubusercontent.com/BrandBoostApp/image-/main/2.jpg" 
                    alt="Logo Résidences Masséna"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-display font-bold text-white">
                  Résidences Masséna Inc.
                </h3>
              </div>
              <p className="text-white/90 text-sm leading-relaxed">
                La Maison du Bien-être - Accompagnement personnalisé pour les personnes en déficience intellectuelle
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-white text-sm transition-colors inline-block hover:translate-x-1 transform duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-semibold mb-4">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3 text-sm">
                  <MapPin className="w-4 h-4 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white/90">
                    444 Avenue Yvon-Berger<br />
                    Laval, Québec H7L 2C2, Canada
                  </span>
                </li>
                <li className="flex items-center space-x-3 text-sm">
                  <Phone className="w-4 h-4 text-white flex-shrink-0" />
                  <a href="tel:+14506282696" className="text-white/90 hover:text-white transition-colors">
                    (450) 628-2696
                  </a>
                </li>
                <li className="flex items-center space-x-3 text-sm">
                  <Mail className="w-4 h-4 text-white flex-shrink-0" />
                  <a href="mailto:admin@residencemassena.com" className="text-white/90 hover:text-white transition-colors">
                    admin@residencemassena.com
                  </a>
                </li>
              </ul>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="text-white font-semibold mb-4">
                {t('footer.followUs')}
              </h4>
              <SocialButtons className="flex flex-wrap gap-3" />

              {/* Newsletter */}
              <div className="mt-6">
                <p className="text-white/90 text-sm mb-3">
                  Abonnez-vous à notre infolettre
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Votre email"
                    className="bg-white/20 border border-white/30 rounded-lg px-4 py-2 text-white placeholder:text-white/70 flex-1 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white/30 hover:bg-white/40 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                  >
                    S'abonner
                  </motion.button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-white/60 text-sm text-center md:text-left">
                © {currentYear} Résidence Massena. {t('footer.rights')}.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className="text-white/60 hover:text-white text-sm transition-colors">
                  {t('footer.privacy')}
                </Link>
                <Link to="/terms" className="text-white/60 hover:text-white text-sm transition-colors">
                  {t('footer.terms')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 glass-btn p-4 rounded-full shadow-glass-lg z-40"
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-6 h-6" />
      </motion.button>
    </footer>
  )
}

export default Footer

